import type { OperationContext } from 'urql';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';

import { SecondaryButtonV2 } from '@fragment/ui/src/components/Button/SecondaryButtonV2/SecondaryButtonV2';
import { InlineError } from '@fragment/ui/src/components/InlineError/InlineError';
import { LoadingText } from '@fragment/ui/src/components/LoadingText';
import { POSTHOG_MASK_CLASS } from '@fragment/ui/src/constants';

import { useGetWorkspacePaymentAccountsQuery } from '../../../../hooks/internalApi';
import { usePaymentAccountVerificationToken } from '../../../../hooks/usePaymentAccountVerificationToken';
import { getResultOrError } from '../../../../utils/errors';
import {
  PaymentAccountVerificationModal,
  type PaymentAccountVerificationSession,
} from './PaymentAccountVerificationModal';

type Props = {
  workspaceId: string;
  context: Partial<OperationContext>;
  hasAccessToken: boolean;
  canVerify: boolean;
  onError: (message: string) => void;
};

export const PaymentAccountSettingsList = ({
  workspaceId,
  context,
  hasAccessToken,
  canVerify,
  onError,
}: Props) => {
  // urql requires stable query variables between renders.
  const variables = useMemo(() => ({ workspaceId }), [workspaceId]);
  const [{ data, error, fetching }, refetch] =
    useGetWorkspacePaymentAccountsQuery({
      variables,
      context,
      pause: !hasAccessToken,
    });
  const workspace = getResultOrError(data?.workspace, error);
  const { createVerificationToken, fetching: isCreatingToken } =
    usePaymentAccountVerificationToken({ workspaceId, context });
  const [verification, setVerification] = useState<
    { ledgerId: string; session: PaymentAccountVerificationSession } | undefined
  >(undefined);
  const refreshAccounts = () => refetch({ requestPolicy: 'network-only' });
  const startVerification = (ledgerId: string) => {
    createVerificationToken(ledgerId)
      .then((session) => setVerification({ ledgerId, session }))
      .catch((cause: unknown) => {
        onError(
          cause instanceof Error
            ? cause.message
            : 'Unable to start verification.'
        );
      });
  };

  const renderAccounts = () => {
    if (fetching && !data)
      return <LoadingText text="Loading payment accounts" />;
    if (workspace.isError || !workspace.result.paymentAccounts)
      return (
        <div role="alert" className="flex flex-col gap-f2">
          <InlineError>Unable to load payment accounts</InlineError>
          <SecondaryButtonV2 disabled={fetching} onClick={refreshAccounts}>
            Retry
          </SecondaryButtonV2>
        </div>
      );
    if (workspace.result.paymentAccounts.length === 0)
      return <p>No payment accounts yet.</p>;
    return (
      <ul className="flex flex-col gap-f4" aria-label="Payment accounts">
        {workspace.result.paymentAccounts.map((account) => (
          <li
            key={account.ledgerId}
            className="flex items-center justify-between gap-f4 border-b border-main-200 pb-f4"
          >
            <div className="flex flex-col gap-f1">
              <span className={POSTHOG_MASK_CLASS}>{account.accountName}</span>
              <span className={clsx(POSTHOG_MASK_CLASS, 'text-main-500')}>
                {account.ledgerName} / {account.accountPath}
              </span>
              {!account.verification && (
                <span className="text-main-500">Not provisioned yet</span>
              )}
            </div>
            {account.verification?.isVerified && (
              <span className="rounded border border-main-300 px-f2 text-main-500">
                Verified
              </span>
            )}
            {!account.verification?.isVerified && canVerify && (
              <SecondaryButtonV2
                disabled={!account.verification || isCreatingToken}
                onClick={() => startVerification(account.ledgerId)}
              >
                Verify
              </SecondaryButtonV2>
            )}
            {!account.verification?.isVerified && !canVerify && (
              <div className="flex flex-col gap-f1 text-main-500">
                <span>Not verified</span>
                <span>Ask an admin to verify your payment account</span>
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      {renderAccounts()}
      {canVerify && verification && (
        <PaymentAccountVerificationModal
          key={verification.ledgerId}
          {...verification}
          createVerificationToken={createVerificationToken}
          onClose={() => setVerification(undefined)}
          onCompleted={() => {
            setVerification(undefined);
            refreshAccounts();
          }}
        />
      )}
    </>
  );
};
